import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Formik, Form, FormikContext, useFormikContext } from 'formik';
import DynamicStepComponent from '../components/form/DynamicStepComponent';
import Step5Diagram from '../components/form/Step5Diagram';
import DeferredCatalogueStep, { useCatalogueRendererReady } from '../components/form/DeferredCatalogueStep';
import { CalendarPreload, useCalendarReady } from '../components/form/DeferredCalendar';
import FormNavigation from '../components/form/FormNavigation';
import { generateYupSchema } from '../utils/validationSchemas';
import DuplicatePatientModal from '../components/DuplicatePatientModal';
import { generatePDF } from '../services/pdfGenerator';
import { loadIntakeDefinition } from '../services/intakeDefinition';
import { sendToBackend } from '../services/api';
import { registerFieldLabels, formatErrorsAsString, getFieldLabel } from '../utils/fieldLabelRegistry';
import { DEFAULT_STATE } from '../utils/usStates';
import '../styles/formPage.css';
import { environment } from '../env/environment';
import { useNavigate } from 'react-router-dom';
import { secureSessionStorage } from '../utils/secure-session';
import * as Yup from 'yup';
import BrandedLogo from '../components/Branding/BrandedLogo';
import activityLogger from '../utils/activityLogger';
import { useTenant } from '../context/TenantContext';
import logger from '../utils/logger';

/**
 * Clean payload by removing empty, null, undefined, false values
 * Only keeps fields with actual meaningful data
 */
const cleanPayload = (data) => {
  if (!data || typeof data !== 'object') return data;

  const cleaned = {};

  Object.entries(data).forEach(([key, value]) => {
    // Skip null, undefined
    if (value === null || value === undefined) return;

    // Skip boolean false (unchecked checkboxes)
    if (value === false) return;

    // Skip NaN
    if (typeof value === 'number' && isNaN(value)) return;

    // Skip string "null", "undefined", "false" or empty strings
    if (typeof value === 'string') {
      const trimmed = value.trim().toLowerCase();
      if (trimmed === '' || trimmed === 'null' || trimmed === 'undefined' || trimmed === 'false') return;
    }

    // Skip empty arrays
    if (Array.isArray(value) && value.length === 0) return;

    // Skip empty objects (but not Date objects)
    if (typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
      if (Object.keys(value).length === 0) return;

      // Recursively clean nested objects
      const cleanedNested = cleanPayload(value);
      if (Object.keys(cleanedNested).length > 0) {
        cleaned[key] = cleanedNested;
      }
      return;
    }

    // Keep 0 (zero) and other valid numbers
    // Keep true booleans
    // Keep non-empty strings
    cleaned[key] = value;
  });

  return cleaned;
};

const convertFieldNameToLowercase = (fieldName) => {
  if (!fieldName) return '';

  const specialMappings = {
    'dateofbirth': 'dateofbirth',
    'date_of_birth': 'dateofbirth',
    'dob': 'dateofbirth',
    'firstname': 'firstname',
    'first_name': 'firstname',
    'lastname': 'lastname', 
    'last_name': 'lastname',
    'postalcode': 'postalcode',
    'postal_code': 'postalcode',
    'currentpainlevel': 'currentpainlevel',
    'current_pain_level': 'currentpainlevel',
    'bestpainlevel': 'bestpainlevel',
    'best_pain_level': 'bestpainlevel',
    'worstpainlevel': 'worstpainlevel',
    'worst_pain_level': 'worstpainlevel',
    'medication_data': 'medicationdata',
    'medicationdata': 'medicationdata',
    'medication_list': 'medicationlist',
    'medicationlist': 'medicationlist',
    'medication_count': 'medicationcount',
    'medicationcount': 'medicationcount',
    'medication_search': 'medicationsearch',
    'medicationsearch': 'medicationsearch',
    'medication_wizard': 'medicationwizard',
    'medicationwizard': 'medicationwizard',
    'pharmacy_data': 'pharmacydata',
    'pharmacydata': 'pharmacydata'
  };
  
  const lowerField = fieldName.toLowerCase();
  
  if (specialMappings[lowerField]) {
    return specialMappings[lowerField];
  }
  
  return lowerField.replace(/[^a-z0-9]/g, '');
};

const getFieldName = (field, index) => {
  if (!field) return `field_${index}`;
  
  if (field.name) {
    return convertFieldNameToLowercase(field.name);
  }
  
  if (field.labelName) {
    return convertFieldNameToLowercase(field.labelName);
  }
  
  if (field.label) {
    return convertFieldNameToLowercase(field.label);
  }
  
  return `field_${index}`;
};

// Storage key for form data persistence
const FORM_STORAGE_KEY = 'empower-form-draft';
const STORAGE_EXPIRY_HOURS = 24;

// Component to auto-save form data as user fills the form
const FormDataPersistence = ({ step, painLevels, canvasGender, onFormModified }) => {
  const { values } = useFormikContext();
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip saving on first render (initial load)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Save form data to sessionStorage
    try {
      const dataToStore = {
        step,
        formValues: values,
        painLevels,
        canvasGender,
        timestamp: Date.now()
      };
      secureSessionStorage.setItem(FORM_STORAGE_KEY, dataToStore).then(() => {
        onFormModified(true);
      });
    } catch (err) {
      logger.error('Error saving form data:', err);
    }
  }, [values, step, painLevels, canvasGender, onFormModified]);

  return null; // This component doesn't render anything
};

const FormPage = () => {
  const navigate = useNavigate();
  const { tenant, loading: tenantLoading, isFeatureEnabled } = useTenant();
  const [step, setStep] = useState(1);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [painLevels, setPainLevels] = useState({ current: 0, worst: 0, best: 0 });
  const [painDiagramPdf, setPainDiagramPdf] = useState(null);
  const [formValues, setFormValues] = useState(null);

  // Duplicate patient check
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicatePatientName, setDuplicatePatientName] = useState('');
  const [duplicateFollowupDomain, setDuplicateFollowupDomain] = useState(null);
  const [error, setError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState('error');
  const [hasValidationError, setHasValidationError] = useState(false);
  const step5Ref = useRef(null);
  const [canvasGender, setCanvasGender] = useState('woman');
  const [fieldsMap, setFieldsMap] = useState({});
  const [sortedModules, setSortedModules] = useState([]);
  const [moduleContainerWidths, setModuleContainerWidths] = useState({});
  const [initializationAttempt, setInitializationAttempt] = useState(0);
  const [restoredFormData, setRestoredFormData] = useState(null);
  const [hasFormBeenModified, setHasFormBeenModified] = useState(false);
  const formikRef = useRef(null);
  const isSubmittingStep = useRef(false); // Prevent duplicate step submissions
  const isSubmittingForm = useRef(false);
  const [apiTenantId, setApiTenantId] = useState(null); // Store tenantId from API response

  // Function to clear stored form data
  const clearStoredFormData = useCallback(() => {
    try {
      sessionStorage.removeItem(FORM_STORAGE_KEY);
      setHasFormBeenModified(false);
    } catch (err) {
      logger.error('Error clearing stored form data:', err);
    }
  }, []);

  // Function to save form data to sessionStorage
  const saveFormDataToStorage = useCallback((values, currentStep, currentPainLevels, currentGender) => {
    try {
      const dataToStore = {
        step: currentStep,
        formValues: values,
        painLevels: currentPainLevels,
        canvasGender: currentGender,
        timestamp: Date.now()
      };
      secureSessionStorage.setItem(FORM_STORAGE_KEY, dataToStore);
    } catch (err) {
      logger.error('Error saving form data to storage:', err);
    }
  }, []);

  // Restore form data on component mount
  useEffect(() => {
    secureSessionStorage.getItem(FORM_STORAGE_KEY).then(parsedData => {
      if (!parsedData) return;

      const expiryTime = STORAGE_EXPIRY_HOURS * 60 * 60 * 1000;
      if (Date.now() - parsedData.timestamp > expiryTime) {
        clearStoredFormData();
        return;
      }

      if (parsedData.step) setStep(parsedData.step);
      if (parsedData.painLevels) setPainLevels(parsedData.painLevels);
      if (parsedData.canvasGender) setCanvasGender(parsedData.canvasGender);
      if (parsedData.formValues) {
        setRestoredFormData(parsedData.formValues);
        setHasFormBeenModified(true);
      }
    }).catch(err => {
      logger.error('Error restoring form data:', err);
      clearStoredFormData();
    });
  }, [clearStoredFormData]);

  // Beforeunload event listener - warn user before refresh/close
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasFormBeenModified) {
        e.preventDefault();
        // Note: Modern browsers (Chrome, Firefox, Safari) ignore custom messages for security
        // but setting a message is still required for the dialog to appear
        const message = 'You have unsaved progress. Are you sure you want to leave?';
        e.returnValue = message; // Required for Chrome and modern browsers
        return message; // Required for some older browsers
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasFormBeenModified]);

  useEffect(() => {
    const handleGlobalError = (event) => activityLogger.browserError(event);
    const handleRejection = (event) => activityLogger.browserError(event.reason || { message: 'Unhandled promise rejection' });
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleRejection);
    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  const getCurrentModuleName = () => {
    if (step >= 1 && step <= sortedModules.length) {
      return sortedModules[step - 1]?.module_name?.toLowerCase() || '';
    }
    return '';
  };

  const currentModuleName = getCurrentModuleName();
  const catalogueRendererReady = useCatalogueRendererReady(sortedModules[step - 1]?.module_type);
  const currentCalendarFields = fieldsMap[sortedModules[step - 1]?.module_name];
  const calendarReady = useCalendarReady(currentCalendarFields);

  
  // Get current module's container width class
  const getCurrentContainerWidthClass = () => {
    if (step >= 1 && step <= sortedModules.length) {
      const currentModule = sortedModules[step - 1];
      const moduleName = currentModule?.module_name?.toLowerCase() || '';
      return moduleContainerWidths[moduleName] || 'wide';
    }
    return 'wide';
  };

  const currentContainerWidth = getCurrentContainerWidthClass();
  
  const handleMedicationPharmacyExtracted = useCallback((pharmacyDetails, setFieldValue) => {
    setFieldValue('extractedPharmacyDetails', pharmacyDetails);
  }, []);

  const handleMedicationAllergiesPharmacyExtracted = useCallback((pharmacyDetails, setFieldValue) => {
    setFieldValue('extractedPharmacyDetailsAllergies', pharmacyDetails);
  }, []);

  // Toast notification function
  const showToastNotification = (message, type = 'error') => {
    setToastMessage(message);
    setToastType(type);
    
    setTimeout(() => {
      setToastMessage(null);
    }, 5000);
  };

  // Toast Notification Component
  const ToastNotification = ({ message, type, onClose }) => {
    if (!message) return null;
    
    const bgColor = type === 'error' ? '#f8d7da' : '#d4edda';
    const borderColor = type === 'error' ? '#f5c6cb' : '#c3e6cb';
    const textColor = type === 'error' ? '#721c24' : '#155724';
    
    return (
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        backgroundColor: bgColor,
        border: `1px solid ${borderColor}`,
        borderRadius: '8px',
        padding: '15px',
        color: textColor,
        zIndex: 10000,
        maxWidth: '400px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        animation: 'slideInRight 0.3s ease-out'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, paddingRight: '10px' }}>
            <strong>{type === 'error' ? 'Error' : 'Success'}</strong>
            <div style={{ marginTop: '5px' }}>{message}</div>
          </div>
          <button 
            onClick={onClose}
            style={{ 
              background: 'none', 
              border: 'none', 
              color: textColor, 
              cursor: 'pointer',
              fontSize: '18px',
              padding: '0',
              lineHeight: '1'
            }}
          >
            ×
          </button>
        </div>
        
        <style jsx>{`
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}</style>
      </div>
    );
  };

  // Error Display Component
  const ErrorDisplay = ({ error, onClose, errorsObject }) => {
    // If errorsObject is provided, use it for dynamic list
    if (errorsObject) {
      const errorEntries = Object.entries(errorsObject);
      if (errorEntries.length === 0) return null;

      return (
        <div style={{
          backgroundColor: '#fee',
          border: '1px solid #fcc',
          borderRadius: '8px',
          padding: '15px',
          margin: '15px 0',
          color: '#c33',
          fontSize: '14px',
          lineHeight: '1.4',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, paddingRight: '10px' }}>
              <strong style={{ display: 'block', marginBottom: '5px' }}>⚠️ Error</strong>
              <span style={{ display: 'block' }}>
                {`Please fix ${errorEntries.length} validation error${errorEntries.length > 1 ? 's' : ''}:`}
              </span>
              {errorEntries.map(([key, msg]) => {
                // Handle multi-line error messages (split by newline)
                if (typeof msg === 'string' && msg.includes('\n')) {
                  return msg.split('\n').map((line, lineIdx) => (
                    <span key={`${key}-${lineIdx}`} style={{ display: 'block' }}>• {line}</span>
                  ));
                }
                return <span key={key} style={{ display: 'block' }}>• {msg}</span>;
              })}
            </div>
            {onClose && (
              <button
                onClick={onClose}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#c33',
                  cursor: 'pointer',
                  fontSize: '18px',
                  padding: '0 5px',
                  lineHeight: '1'
                }}
              >
                ×
              </button>
            )}
          </div>
        </div>
      );
    }

    if (!error) return null;

    return (
      <div style={{
        backgroundColor: '#fee',
        border: '1px solid #fcc',
        borderRadius: '8px',
        padding: '15px',
        margin: '15px 0',
        color: '#c33',
        fontSize: '14px',
        lineHeight: '1.4',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, paddingRight: '10px' }}>
            <strong style={{ display: 'block', marginBottom: '5px' }}>⚠️ Error</strong>
            <span style={{ whiteSpace: 'pre-wrap', display: 'block' }}>{error}</span>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: '#c33',
                cursor: 'pointer',
                fontSize: '18px',
                padding: '0 5px',
                lineHeight: '1'
              }}
            >
              ×
            </button>
          )}
        </div>
      </div>
    );
  };


  // Skeleton Loading Component - Better UX than spinner
  const SkeletonLoading = () => (
    <div className="skeleton-container">
      <div className="skeleton-header">
        <div className="skeleton skeleton-logo"></div>
        <div className="skeleton skeleton-progress" style={{ maxWidth: '300px' }}></div>
      </div>
      <div className="skeleton-form">
        <div className="skeleton skeleton-title" style={{ alignSelf: 'center' }}></div>
        <div className="skeleton-row">
          <div className="skeleton-field">
            <div className="skeleton skeleton-label"></div>
            <div className="skeleton skeleton-input"></div>
          </div>
          <div className="skeleton-field">
            <div className="skeleton skeleton-label"></div>
            <div className="skeleton skeleton-input"></div>
          </div>
        </div>
        <div className="skeleton-row">
          <div className="skeleton-field">
            <div className="skeleton skeleton-label"></div>
            <div className="skeleton skeleton-input"></div>
          </div>
          <div className="skeleton-field">
            <div className="skeleton skeleton-label"></div>
            <div className="skeleton skeleton-input"></div>
          </div>
        </div>
        <div className="skeleton-field">
          <div className="skeleton skeleton-label"></div>
          <div className="skeleton skeleton-input" style={{ height: '100px' }}></div>
        </div>
        <div className="skeleton-buttons">
          <div className="skeleton skeleton-button"></div>
          <div className="skeleton skeleton-button"></div>
        </div>
      </div>
    </div>
  );

  // Compact Progress Indicator - Minimal dots design
  const StepProgressIndicator = ({ currentStep, totalSteps, moduleNames }) => {
    return (
      <div className="progress-compact">
        {/* Dot indicators - only active shows label */}
        <div className="progress-dots">
          {moduleNames.map((name, index) => {
            const stepNum = index + 1;
            const isActive = stepNum === currentStep;
            const isCompleted = stepNum < currentStep;

            return (
              <div
                key={index}
                className={`progress-dot-item ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                title={name}
              >
                <div className={`progress-dot ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}>
                  {isCompleted && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  )}
                </div>
                {/* Only show label for active step */}
                {isActive && (
                  <span className="progress-dot-label">{name}</span>
                )}
              </div>
            );
          })}
        </div>
        {/* Simple step counter */}
        <div className="progress-counter">
          {currentStep} / {totalSteps}
        </div>
      </div>
    );
  };

  // Legacy Loading Spinner (for submission overlay)
  const LoadingSpinner = ({ message = "Loading..." }) => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '20px',
      gap: '10px'
    }}>
      <div style={{
        border: '3px solid #f3f3f3',
        borderTop: '3px solid var(--color-primary, #2563eb)',
        borderRadius: '50%',
        width: '30px',
        height: '30px',
        animation: 'spin 1s linear infinite'
      }}></div>
      <div>{message}</div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );

  useEffect(() => {
    if (tenantLoading) return;
    let active = true;
    setIsInitialLoading(true);
    setError(null);

    const initialize = async () => {
      try {
        const tenantId = tenant?.id || window.__TENANT_ID__ || localStorage.getItem('dev_tenant_id');
        const tenantSlug = new URLSearchParams(window.location.search).get('tenant') || localStorage.getItem('dev_tenant') || tenant?.slug;
        const data = await loadIntakeDefinition({ baseUrl: environment.baseUrl, tenantId, tenantSlug });
        if (!active) return;

        // Keep the complete definition barrier. Formik never receives a
        // partial field map that could reset entered or restored values.
        const sorted = [...data.modules].sort((a, b) => a.count - b.count);
        const map = Object.fromEntries(sorted.map(mod => {
          const fields = data.fieldDefinitions[mod.module_name].fields?.map(field => {
            if (field.type === 'radio' && field.optionsWithConditional) {
              return {
                ...field,
                optionsWithConditional: field.optionsWithConditional.map((cond, index) => ({
                  ...cond, optionValue: field.options[index]
                }))
              };
            }
            return field;
          }) || [];
          return [mod.module_name, fields];
        }));
        const widths = Object.fromEntries(sorted.map(mod => [
          mod.module_name.toLowerCase(), data.fieldDefinitions[mod.module_name].containerWidth || 'wide'
        ]));
        Object.values(map).forEach(fields => registerFieldLabels(fields));
        setModules(sorted);
        setSortedModules(sorted);
        setModuleContainerWidths(widths);
        setFieldsMap(map);
        if (data.tenantId) {
          setApiTenantId(data.tenantId);
          window.__TENANT_ID__ = data.tenantId;
          localStorage.setItem('dev_tenant_id', String(data.tenantId));
        }
      } catch (err) {
        if (active) {
          logger.error('Failed to initialize form:', err);
          setError(`Failed to load form: ${err.message}`);
        }
      } finally {
        if (active) setIsInitialLoading(false);
      }
    };
    initialize();
    return () => { active = false; };
  }, [tenantLoading, tenant?.id, initializationAttempt]);

  // Dynamic initial values generation
  const dynamicInitialValues = useMemo(() => {
    const dynamicValues = {};
    
    Object.values(fieldsMap).forEach(fields => {
      fields.forEach((field, index) => {
        const fieldName = getFieldName(field, index);
        
        switch (field.type) {
          case 'text':
          case 'textarea':
          case 'email':
          case 'tel':
          case 'phone':
          case 'search':
          case 'date':
            dynamicValues[fieldName] = field.defaultValue || '';
            break;
          case 'checkbox':
            if (field.options && field.options.length > 0) {
              dynamicValues[fieldName] = field.defaultValue || [];
            } else {
              dynamicValues[fieldName] = field.defaultValue || false;
            }
            break;
          case 'radio':
          case 'select':
            dynamicValues[fieldName] = field.defaultValue || '';
            break;
          case 'multiselect':
            dynamicValues[fieldName] = field.defaultValue || [];
            break;
          case 'number':
            dynamicValues[fieldName] = field.defaultValue !== undefined ? field.defaultValue : '';
            break;
          case 'range':
            // ✅ FIX: ALWAYS use empty string for range/slider fields
            // This ensures validation fails until user actually interacts with the slider
            // The slider component will DISPLAY the visual default (0) but form value stays empty
            // This is critical for pain level GROUP validation to work correctly
            dynamicValues[fieldName] = '';
            break;
          case 'file':
          case 'image':
            dynamicValues[fieldName] = field.defaultValue || null;
            break;
          case 'authorized_contacts':
            // Initialize with one empty contact
            dynamicValues[fieldName] = [{
              id: Date.now(),
              firstName: '',
              lastName: '',
              email: '',
              phone: '',
              relationship: '',
              otherRelationship: '',
              isEmergencyContact: false,
              isAuthorizedMedical: false
            }];
            break;
          case 'address':
            // ✅ Composite Address field - initialize all subfields
            // Default state is Oklahoma (OK) unless configured otherwise in admin
            const addressConfig = field.addressConfig || {};
            dynamicValues[`${fieldName}_street`] = '';
            dynamicValues[`${fieldName}_suite`] = '';
            dynamicValues[`${fieldName}_city`] = '';
            dynamicValues[`${fieldName}_state`] = addressConfig.state?.defaultValue || DEFAULT_STATE;
            dynamicValues[`${fieldName}_country`] = 'US';
            break;
          default:
            dynamicValues[fieldName] = field.defaultValue || '';
        }

        if (field.optionsWithConditional && Array.isArray(field.optionsWithConditional)) {
          field.optionsWithConditional.forEach((config, configIndex) => {
            if (config.conditionalInputs && Array.isArray(config.conditionalInputs)) {
              config.conditionalInputs.forEach((conditionalInput, conditionalIndex) => {
                const conditionalFieldName = `${fieldName}_conditional_${configIndex}_${conditionalIndex}`;

                switch (conditionalInput.type) {
                  case 'file':
                    dynamicValues[conditionalFieldName] = [];
                    break;
                  case 'separateSection':
                    // Initialize section fields
                    dynamicValues[conditionalFieldName] = '';
                    if (conditionalInput.sectionFields && Array.isArray(conditionalInput.sectionFields)) {
                      // Check if user created multiple radio fields with labels (old pattern)
                      const radioFieldsWithLabels = conditionalInput.sectionFields.filter(
                        sf => sf.type === 'radio' && sf.label && (!sf.options || sf.options.length === 0)
                      );

                      // Initialize combined field if multiple radio labels exist
                      if (radioFieldsWithLabels.length > 1) {
                        dynamicValues[`${conditionalFieldName}_section_combined`] = '';
                      }

                      conditionalInput.sectionFields.forEach((sectionField, sfIndex) => {
                        const sectionFieldName = `${conditionalFieldName}_section_${sfIndex}`;
                        if (sectionField.type === 'checkbox') {
                          dynamicValues[sectionFieldName] = [];
                        } else {
                          dynamicValues[sectionFieldName] = '';
                        }
                      });
                    }
                    break;
                  case 'textarea':
                  case 'text':
                  default:
                    dynamicValues[conditionalFieldName] = '';
                }
              });
            }
          });
        }
      });
    });
    
    dynamicValues['medicationdata'] = {
      uploadedFiles: [],
      cameraFiles: [],
      extractedText: {},
      extractedMedicationsFormatted: '',
      extractedMedicationsCount: 0,
      searchResults: [],
      searchResultsFormatted: '',
      searchResultsCount: 0,
      wizardSelections: [],
      wizardSelectionsFormatted: '',
      wizardSelectionsCount: 0,
      selectedPharmacy: null,
      pharmacyFormatted: '',
      totalMedicationsCount: 0,
      timestamp: null
    };
    
    dynamicValues['medicationlist'] = '';
    dynamicValues['medicationcount'] = 0;
    dynamicValues['medicationsearch'] = '';
    dynamicValues['medicationwizard'] = '';
    dynamicValues['pharmacydata'] = '';
    dynamicValues['manualMedications'] = [];
    dynamicValues['manualAllergies'] = [];
    
    // ✅ FIX: Pain levels should start as empty string (not 0)
    // This ensures validation fails until user actually interacts with the slider
    // The slider will DISPLAY 0 visually, but form value stays empty for validation
    dynamicValues['currentpainlevel'] = '';
    dynamicValues['worstpainlevel'] = '';
    dynamicValues['bestpainlevel'] = '';

    // Merge with restored form data if available
    if (restoredFormData) {
      return { ...dynamicValues, ...restoredFormData };
    }

    return dynamicValues;
  }, [fieldsMap, restoredFormData]);

  // const isPainDiagramEnabled = isFeatureEnabled('pain_diagram');
  const isPainDiagramEnabled = false;
  const totalSteps = sortedModules.length + (isPainDiagramEnabled ? 1 : 0);

  useEffect(() => {
    if (sortedModules.length > 0) {
      const tid = apiTenantId || tenant?.id || window.__TENANT_ID__ || localStorage.getItem('dev_tenant_id');
      activityLogger.setContext({ tenantId: tid });
      activityLogger.formStarted();
      activityLogger.stepStarted(1, sortedModules[0]?.module_name || 'Step 1');
    }
  }, [sortedModules.length > 0]);

  const prevStep = () => {
    if (step > 1) {
      setStep(s => s - 1);
      setSubmitError(null);
      setHasValidationError(false);
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    }
  };

  const processROSFields = (values, fieldsMap) => {
    const processedValues = { ...values };
    const rosFieldsToDelete = [];
      
    Object.entries(fieldsMap).forEach(([moduleName, fields]) => {
      fields.forEach(field => {
        if (field.type === 'ros' && field.field_name) {
          const fieldName = convertFieldNameToLowercase(field.field_name);
          const selectedValues = values[fieldName];
          if (selectedValues && Array.isArray(selectedValues)) {

            field.optionsWithConditional?.forEach((optionConfig) => {
              const optionValue = optionConfig.value || optionConfig.label;
              
              if (optionConfig.conditionalInputs?.length > 0) {
                optionConfig.conditionalInputs.forEach((conditionalField, index) => {
                  const conditionalFieldName = `${fieldName}_${optionValue}_${conditionalField.id || index}`;
                  rosFieldsToDelete.push(conditionalFieldName);
                });
              }
            });
          }
        }
      });
    });
    
    rosFieldsToDelete.forEach(fieldToDelete => {
      if (processedValues[fieldToDelete] !== undefined) {
        delete processedValues[fieldToDelete];
      }
    });
    
    return processedValues;
  };

  /**
   * Process accordion fields to aggregate checkbox selections into proper structure
   * Converts individual checkbox booleans into array of selected labels
   */
  const processAccordionFields = (values, fieldsMap) => {
    const processedValues = { ...values };
    const fieldsToRemove = [];

    Object.entries(fieldsMap).forEach(([moduleName, fields]) => {
      fields.forEach(field => {
        if (field.type === 'accordion' && field.accordionSections) {
          const baseFieldName = convertFieldNameToLowercase(field.field_name || field.name || field.labelName);

          // Process each section
          field.accordionSections.forEach((section, sectionIndex) => {
            const sectionFieldName = `${baseFieldName}_section_${sectionIndex}`;
            const selectedValues = [];
            const nestedInputData = {};

            // Collect checked checkbox labels
            section.checkboxes?.forEach((checkbox, checkboxIndex) => {
              const checkboxFieldName = `${sectionFieldName}_checkbox_${checkboxIndex}`;
              const checkboxFieldNameLower = convertFieldNameToLowercase(checkboxFieldName);

              // Check if this checkbox is selected
              if (values[checkboxFieldName] === true || values[checkboxFieldNameLower] === true) {
                selectedValues.push(checkbox.label);

                // Collect nested input values if any
                if (checkbox.nestedInputs?.length > 0) {
                  checkbox.nestedInputs.forEach((nestedInput, inputIndex) => {
                    const nestedFieldName = `${checkboxFieldName}_input_${inputIndex}`;
                    const nestedFieldNameLower = convertFieldNameToLowercase(nestedFieldName);
                    const nestedValue = values[nestedFieldName] || values[nestedFieldNameLower];

                    if (nestedValue && nestedValue !== '' && nestedValue !== null) {
                      nestedInputData[`${checkbox.label} - ${nestedInput.label}`] = nestedValue;
                    }

                    // Mark for removal
                    fieldsToRemove.push(nestedFieldName);
                    fieldsToRemove.push(nestedFieldNameLower);
                  });
                }
              }

              // Mark checkbox field for removal
              fieldsToRemove.push(checkboxFieldName);
              fieldsToRemove.push(checkboxFieldNameLower);
            });

            // Only add to payload if there are selected values
            if (selectedValues.length > 0) {
              const sectionSlug = section.title.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/^_|_$/g, '');
              processedValues[`accordion_${sectionSlug}`] = {
                question: section.title,
                selectedValues: selectedValues,
                ...(Object.keys(nestedInputData).length > 0 && { additionalInfo: nestedInputData })
              };
            }
          });
        }
      });
    });

    // Remove individual checkbox fields
    fieldsToRemove.forEach(fieldName => {
      delete processedValues[fieldName];
    });

    return processedValues;
  };

  /**
   * Transform authorized_contacts array to individual backend fields
   * Converts: { authorizedcontacts: [{firstName: "John", ...}, ...] }
   * To: { authorizedcontact1firstname: "John", authorizedcontact1lastname: "Doe", ... }
   */
  const transformAuthorizedContactsForBackend = (values, fieldsMap) => {
    const processedValues = { ...values };
    const fieldsToDelete = [];

    // Helper function to transform contacts array
    const transformContacts = (fieldName, contacts) => {
      if (!contacts || !Array.isArray(contacts)) return;

      contacts.forEach((contact, contactIndex) => {
        if (!contact || typeof contact !== 'object') return;

        // Check if contact has any data
        const hasData = contact.firstName || contact.lastName || contact.email || contact.phone;
        if (!hasData) return;

        const contactNum = contactIndex + 1;
        // Use lowercase format: authorizedcontact1firstname
        const prefix = `authorizedcontact${contactNum}`;

        // Map each contact field separately (lowercase keys)
        if (contact.firstName) {
          processedValues[`${prefix}firstname`] = contact.firstName;
        }
        if (contact.lastName) {
          processedValues[`${prefix}lastname`] = contact.lastName;
        }
        if (contact.email) {
          processedValues[`${prefix}email`] = contact.email;
        }
        if (contact.phone) {
          processedValues[`${prefix}phone`] = contact.phone;
        }
        if (contact.relationship) {
          const relationshipValue = contact.relationship === 'Other' && contact.otherRelationship
            ? `Other - ${contact.otherRelationship}`
            : contact.relationship;
          processedValues[`${prefix}relationship`] = relationshipValue;
        }
        // Role flags
        processedValues[`${prefix}isemergency`] = contact.isEmergencyContact ? 'Yes' : 'No';
        processedValues[`${prefix}ismedicalauth`] = contact.isAuthorizedMedical ? 'Yes' : 'No';
      });

      // Add total count
      const validContacts = contacts.filter(c => c && (c.firstName || c.lastName || c.email || c.phone));
      processedValues['authorizedcontactscount'] = validContacts.length;

      // Mark original field for deletion
      fieldsToDelete.push(fieldName);
    };

    // Method 1: Find by fieldsMap type
    Object.entries(fieldsMap).forEach(([moduleName, fields]) => {
      fields.forEach(field => {
        if (field.type === 'authorized_contacts' || field.fieldType === 'authorized_contacts') {
          const fieldName = convertFieldNameToLowercase(field.labelName || field.field_name || field.name || '');
          const contacts = processedValues[fieldName];
          if (contacts && Array.isArray(contacts)) {
            transformContacts(fieldName, contacts);
          }
        }
      });
    });

    // Method 2: Also check for any field that looks like authorized contacts array directly in values
    Object.keys(processedValues).forEach(key => {
      const lowerKey = key.toLowerCase();
      // Check for authorizedcontacts, authorized_contacts, or any variation
      const isAuthorizedContactsField =
        lowerKey === 'authorizedcontacts' ||
        lowerKey === 'authorized_contacts' ||
        lowerKey === 'authorizedcontact' ||
        (lowerKey.includes('authorizedcontact') && !lowerKey.match(/authorizedcontact\d/));

      if (isAuthorizedContactsField) {
        const value = processedValues[key];
        if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
          logger.log('🎯 Found authorized contacts array at key:', key, 'with', value.length, 'contacts');
          transformContacts(key, value);
        }
      }
    });

    // Delete original array fields
    fieldsToDelete.forEach(fieldName => {
      delete processedValues[fieldName];
    });

    logger.log('🔄 Transformed authorized contacts:', {
      deletedFields: fieldsToDelete,
      newFields: Object.keys(processedValues).filter(k => k.startsWith('authorizedcontact'))
    });

    return processedValues;
  };

  const handleSubmit = async (values, actions = null) => {
    // React's disabled state is not synchronous with concurrent validation.
    if (isSubmittingForm.current) return;
    isSubmittingForm.current = true;
    let submitted = false;
    try {
      const submissionStartTime = performance.now();
      setSubmitError(null);

      const transformedValues = {};
      Object.entries(values).forEach(([key, value]) => {
        const lowercaseKey = convertFieldNameToLowercase(key);
        transformedValues[lowercaseKey] = value;
      });

      // Process ROS fields (keeps original checkbox values for PDF)
      const pdfValues = processROSFields(transformedValues, fieldsMap);

      // Process accordion fields for backend (aggregates checkbox values)
      let backendValues = processROSFields(transformedValues, fieldsMap);
      backendValues = processAccordionFields(backendValues, fieldsMap);
      // Transform authorized_contacts to readable format (authorizedContact1FirstName, etc.)
      backendValues = transformAuthorizedContactsForBackend(backendValues, fieldsMap);

      if (!pdfValues?.firstname) {
        const errorMsg = "Required field 'First Name' is missing.";
        setSubmitError(errorMsg);
        showToastNotification(errorMsg, 'error');
        if (actions?.setStatus) actions.setStatus({ general: errorMsg });
        return;
      }

      const formatDate = (dateObj) =>
        `${String(dateObj.getMonth() + 1).padStart(2, '0')}/${String(dateObj.getDate()).padStart(2, '0')}/${dateObj.getFullYear()}`;

      // Format dates for both PDF and backend
      if (pdfValues.injurydate instanceof Date && !isNaN(pdfValues.injurydate)) {
        pdfValues.injurydate = formatDate(pdfValues.injurydate);
        backendValues.injurydate = pdfValues.injurydate;
      }
      if (pdfValues.dateofbirth instanceof Date && !isNaN(pdfValues.dateofbirth)) {
        pdfValues.dateofbirth = formatDate(pdfValues.dateofbirth);
        backendValues.dateofbirth = pdfValues.dateofbirth;
      }

      const validationEndTime = performance.now();
      const validationTime = ((validationEndTime - submissionStartTime) / 1000).toFixed(2);

      setLoading(true);
      activityLogger.submitStarted();

      const pdfStartTime = performance.now();
      let pdfBlob;
      try {
        const pdfPromise = generatePDF(painLevels, pdfValues, sortedModules, fieldsMap, false, tenant?.logoUrl);
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('PDF generation timed out. Your device may not have enough memory. Please try on a desktop browser.')), 60000)
        );
        pdfBlob = await Promise.race([pdfPromise, timeoutPromise]);
      } catch (pdfErr) {
        activityLogger.submitPdfError(pdfErr);
        throw pdfErr;
      }
      const pdfEndTime = performance.now();
      const pdfGenerationTime = ((pdfEndTime - pdfStartTime) / 1000).toFixed(2);

      const backendStartTime = performance.now();
      const backendResponse = await sendToBackend(pdfBlob, backendValues, painDiagramPdf, painLevels);
      submitted = true;
      const backendEndTime = performance.now();
      const backendSubmissionTime = ((backendEndTime - backendStartTime) / 1000).toFixed(2);

      const totalTime = ((backendEndTime - submissionStartTime) / 1000).toFixed(2);

      // Clear stored form data on successful submission
      clearStoredFormData();
      activityLogger.submitCompleted();

      const timingReport = `Form submitted successfully!\n\n⏱️ Timing Report:\n• Validation & Processing: ${validationTime}s\n• PDF Generation: ${pdfGenerationTime}s\n• Backend Submission: ${backendSubmissionTime}s\n• Total Time: ${totalTime}s`;
      showToastNotification(timingReport, 'success');
      navigate('/thank-you', {
        state: {
          timingData: {
            validationTime,
            pdfGenerationTime,
            backendSubmissionTime,
            totalTime,
            backendDetails: backendResponse?.timings || {},
            fileDetails: backendResponse?.fileDetails || {}
          },
          asyncProcessing: backendResponse?.asyncProcessing || null
        }
      });

    } catch (error) {
      logger.error('Form submission error:', error);
      activityLogger.submitApiError(error);
      const errMsg = error.message || 'There was an error processing your form. Please try again.';
      setSubmitError(errMsg);
      showToastNotification(errMsg, 'error');
      if (actions?.setStatus) actions.setStatus({ general: errMsg });
    } finally {
      // Keep a successful submission locked until this form unmounts; allow
      // retry after validation, PDF import/generation or network failure.
      if (!submitted) isSubmittingForm.current = false;
      setLoading(false);
    }
  };

  const handleNextStep = async (step, values, validateForm, setTouched) => {
    // Prevent duplicate submissions
    if (isSubmittingStep.current) {
      logger.warn('Step submission already in progress, ignoring duplicate call');
      return;
    }

    try {
      isSubmittingStep.current = true;
      setIsLoading(true);
      setSubmitError(null);
      setHasValidationError(false);

      const formErrors = await validateForm();

      if (Object.keys(formErrors).length > 0) {
        setHasValidationError(true);
        const touchedFields = Object.keys(formErrors).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {});
        setTouched(touchedFields);

        setSubmitError(true);
        activityLogger.stepValidationError(step, sortedModules[step - 1]?.module_name, formErrors);

        window.scrollTo({ top: 0, behavior: 'smooth' });

        setTimeout(() => {
          const firstErrorField = Object.keys(formErrors)[0];
          const errorElement = document.querySelector(
            `[name="${firstErrorField}"], #${firstErrorField}, [for="${firstErrorField}"]`
          );

          if (errorElement) {
            errorElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest'
            });

            setTimeout(() => {
              const focusTarget = errorElement.tagName === 'LABEL'
                ? document.getElementById(firstErrorField)
                : errorElement;

              if (focusTarget && typeof focusTarget.focus === 'function') {
                focusTarget.focus({ preventScroll: true });

                focusTarget.style.outline = '2px solid #ff6b6b';
                focusTarget.style.outlineOffset = '2px';

                setTimeout(() => {
                  focusTarget.style.outline = '';
                  focusTarget.style.outlineOffset = '';
                }, 3000);
              }
            }, 500);
          }
        }, 1000);

        // Reset flags on validation error
        setIsLoading(false);
        isSubmittingStep.current = false;
        return;
      }

      setHasValidationError(false);

      const transformedValues = {};
      Object.entries(values).forEach(([key, value]) => {
        const lowercaseKey = convertFieldNameToLowercase(key);
        transformedValues[lowercaseKey] = value;
      });

      const currentModule = sortedModules[step - 1];
      const moduleName = currentModule?.module_name?.toLowerCase() || '';

      if (moduleName === 'patient onboarding details' || moduleName === 'new patient onboarding') {
        // ── Duplicate patient check (Step 1 only) ──
        try {
          const webhookTenantId = apiTenantId || tenant?.id || window.__TENANT_ID__ || localStorage.getItem('dev_tenant_id');
          const dupHeaders = { 'Content-Type': 'application/json' };
          if (webhookTenantId) dupHeaders['X-Tenant-ID'] = String(webhookTenantId);
          const dupRes = await fetch(`${environment.baseUrl}/api/patients/check-existing`, {
            method: 'POST', headers: dupHeaders,
            body: JSON.stringify({ email: transformedValues.email, phone: transformedValues.phone }),
          });
          if (dupRes.ok) {
            const dupData = await dupRes.json();
            if (dupData.exists) {
              setDuplicatePatientName(dupData.patientName || '');
              setDuplicateFollowupDomain(dupData.followupDomain || null);
              setShowDuplicateModal(true);
              isSubmittingStep.current = false;
              return;
            }
          }
        } catch (dupErr) {
          logger.warn('[FormPage] Duplicate check failed, continuing:', dupErr.message);
        }

      }

      // ── Save step data to server (DB + GHL + webhook fallback) — ALL steps ──
      try {
        const stepTenantId = apiTenantId || tenant?.id || window.__TENANT_ID__ || localStorage.getItem('dev_tenant_id');
        const stepHeaders = { 'Content-Type': 'application/json' };
        if (stepTenantId) stepHeaders['X-Tenant-ID'] = String(stepTenantId);

        const SKIP_SAVE_KEYS = new Set(['tc', 'tenant_id', 'image', 'textelement']);
        const stepFormData = {};
        for (const [key, value] of Object.entries(transformedValues)) {
          if (SKIP_SAVE_KEYS.has(key)) continue;
          if (typeof value === 'string' && value.startsWith('data:image/')) continue;
          if (typeof value === 'string' && value.length > 50000) continue;
          if (value === '' || value === null || value === undefined) continue;
          if (Array.isArray(value) && value.length === 1 && typeof value[0] === 'object') {
            const allEmpty = Object.values(value[0]).every(v => v === '' || v === false || v === 0);
            if (allEmpty) continue;
          }
          stepFormData[key] = value;
        }

        if (painLevels && (moduleName.includes('history') || moduleName.includes('hpi') || moduleName.includes('pain'))) {
          if (stepFormData.currentpainlevel === undefined) stepFormData.currentpainlevel = painLevels.current;
          if (stepFormData.bestpainlevel === undefined) stepFormData.bestpainlevel = painLevels.best;
          if (stepFormData.worstpainlevel === undefined) stepFormData.worstpainlevel = painLevels.worst;
        }

        const stepPayload = {
          email: transformedValues.email || values.email || values.Email,
          firstname: transformedValues.firstname || values.firstname,
          lastname: transformedValues.lastname || values.lastname,
          phone: transformedValues.phone || values.phone,
          postalcode: transformedValues.postalcode || values.postalcode,
          stepNumber: step,
          stepName: currentModule?.module_name || `Step ${step}`,
          formData: cleanPayload(stepFormData),
          tenantId: stepTenantId,
        };

        logger.log(`[FormPage] Saving step ${step} data (${currentModule?.module_name || '?'})`);
        const body = JSON.stringify(stepPayload);
        let saveRes;
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            saveRes = await fetch(`${environment.baseUrl}/api/save-step-data`, {
              method: 'POST',
              headers: stepHeaders,
              body,
            });
            if (saveRes.ok) break;
            logger.warn(`[FormPage] Step save returned ${saveRes.status} (attempt ${attempt + 1})`);
          } catch (retryErr) {
            if (attempt < 2) {
              await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
              continue;
            }
            throw retryErr;
          }
        }
      } catch (stepErr) {
        logger.warn('[FormPage] Step save failed (non-blocking):', stepErr.message);
        activityLogger.stepSaveError(step, currentModule?.module_name, stepErr);
      }

      activityLogger.setContext({ email: transformedValues.email, patientName: `${transformedValues.firstname || ''} ${transformedValues.lastname || ''}`.trim() });
      activityLogger.stepCompleted(step, currentModule?.module_name);

      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
      setStep(s => s + 1);
      activityLogger.stepStarted(step + 1, sortedModules[step]?.module_name || `Step ${step + 1}`);
      showToastNotification('Step completed successfully!', 'success');
      
    } catch (err) {
      logger.error('Error on step submission:', err);
      const errorMessage = `Error moving to next step: Please check Internet Connection & Internal Server`;
      setSubmitError(errorMessage);
      showToastNotification(errorMessage, 'error');
      
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
      
    } finally {
      setIsLoading(false);
      isSubmittingStep.current = false; // Reset submission flag
    }
  };

  const handleStep5Submit = async (values, validateForm, setTouched) => {
    try {
      setSubmitError(null);
      setHasValidationError(false);

      const formErrors = await validateForm();

      if (Object.keys(formErrors).length > 0) {
        setHasValidationError(true);
        const touchedFields = Object.keys(formErrors).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {});
        setTouched(touchedFields);

        setSubmitError(true);

        window.scrollTo({ top: 0, behavior: 'smooth' });

        setTimeout(() => {
          const firstErrorField = Object.keys(formErrors)[0];
          const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            errorElement.focus();
          }
        }, 1000);
      } else {
        const transformedValues = {};
        Object.entries(values).forEach(([key, value]) => {
          const lowercaseKey = convertFieldNameToLowercase(key);
          transformedValues[lowercaseKey] = value;
        });
        
        setFormValues(transformedValues);
        activityLogger.stepCompleted(step, sortedModules[step - 1]?.module_name);
        if (step5Ref.current?.showPopup) {
          step5Ref.current.showPopup();
        }
      }
    } catch (error) {
      logger.error('Step 5 submission error:', error);
      const errorMessage = `Error in final step: ${error.message}`;
      setSubmitError(errorMessage);
      showToastNotification(errorMessage, 'error');
    }
  };

  const handleSubmitWithPopup = async (diagramPdf) => {
    if (!formValues) {
      const errorMsg = 'Form values are missing. Please try again.';
      setSubmitError(errorMsg);
      showToastNotification(errorMsg, 'error');
      return;
    }

    if (isSubmittingForm.current) return;
    isSubmittingForm.current = true;
    let submitted = false;
    try {
      const submissionStartTime = performance.now();
      setLoading(true);
      setSubmitError(null);
      activityLogger.submitStarted();

      // Process ROS fields (keeps original checkbox values for PDF)
      const pdfValues = processROSFields(formValues, fieldsMap);

      // Process accordion fields for backend (aggregates checkbox values)
      let backendValues = processROSFields(formValues, fieldsMap);
      backendValues = processAccordionFields(backendValues, fieldsMap);
      // Transform authorized_contacts to individual fields
      backendValues = transformAuthorizedContactsForBackend(backendValues, fieldsMap);

      // Get pain diagram drawing data for storage (coordinates, shapes, etc.)
      if (step5Ref.current?.getDrawingData) {
        const drawingData = step5Ref.current.getDrawingData();
        if (drawingData) {
          backendValues.painDiagramData = drawingData;
        }
      }

      const formatDate = (dateObj) =>
        `${String(dateObj.getMonth() + 1).padStart(2, '0')}/${String(dateObj.getDate()).padStart(2, '0')}/${dateObj.getFullYear()}`;

      // Format dates for both PDF and backend
      if (pdfValues.injurydate instanceof Date && !isNaN(pdfValues.injurydate)) {
        pdfValues.injurydate = formatDate(pdfValues.injurydate);
        backendValues.injurydate = pdfValues.injurydate;
      }
      if (pdfValues.dateofbirth instanceof Date && !isNaN(pdfValues.dateofbirth)) {
        pdfValues.dateofbirth = formatDate(pdfValues.dateofbirth);
        backendValues.dateofbirth = pdfValues.dateofbirth;
      }

      const validationEndTime = performance.now();
      const validationTime = ((validationEndTime - submissionStartTime) / 1000).toFixed(2);

      const pdfStartTime = performance.now();
      let pdfBlob;
      try {
        const pdfPromise = generatePDF(painLevels, pdfValues, sortedModules, fieldsMap, false, tenant?.logoUrl);
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('PDF generation timed out. Your device may not have enough memory. Please try on a desktop browser.')), 60000)
        );
        pdfBlob = await Promise.race([pdfPromise, timeoutPromise]);
      } catch (pdfErr) {
        activityLogger.submitPdfError(pdfErr);
        throw pdfErr;
      }
      const pdfEndTime = performance.now();
      const pdfGenerationTime = ((pdfEndTime - pdfStartTime) / 1000).toFixed(2);

      if (diagramPdf) {
        const backendStartTime = performance.now();
        const backendResponse = await sendToBackend(pdfBlob, backendValues, diagramPdf, painLevels);
        submitted = true;

        const backendEndTime = performance.now();
        const backendSubmissionTime = ((backendEndTime - backendStartTime) / 1000).toFixed(2);

        const totalTime = ((backendEndTime - submissionStartTime) / 1000).toFixed(2);

        // Clear stored form data on successful submission
        clearStoredFormData();
        activityLogger.submitCompleted();

        const timingReport = `Form submitted successfully!\n\n⏱️ Timing Report:\n• Validation & Processing: ${validationTime}s\n• PDF Generation: ${pdfGenerationTime}s\n• Backend Submission: ${backendSubmissionTime}s\n• Total Time: ${totalTime}s`;
        showToastNotification(timingReport, 'success');
        navigate('/thank-you', {
          state: {
            timingData: {
              validationTime,
              pdfGenerationTime,
              backendSubmissionTime,
              totalTime,
              backendDetails: backendResponse?.timings || {},
              fileDetails: backendResponse?.fileDetails || {}
            },
            asyncProcessing: backendResponse?.asyncProcessing || null
          }
        });

      } else {
        const errorMsg = 'Pain diagram could not be generated. Please try again.';
        setSubmitError(errorMsg);
        showToastNotification(errorMsg, 'error');
      }
    } catch (error) {
      logger.error('Final submission error:', error);
      activityLogger.submitApiError(error);
      const errorMsg = `There was an error processing your form: ${error.message}`;
      setSubmitError(errorMsg);
      showToastNotification(errorMsg, 'error');
    } finally {
      if (!submitted) isSubmittingForm.current = false;
      setLoading(false);
    }
  };

  const renderStep = (step, props) => {
    const sharedProps = {
      ...props,
      setPainLevels,
      setPainDiagramPdf,
      canvasGender,
      setCanvasGender,
      getFieldName,
    };

    if (step >= 1 && step <= sortedModules.length) {
      const currentModule = sortedModules[step - 1];
      const currentFields = fieldsMap[currentModule?.module_name] || [];

      // Check by module_type instead of module_name for built-in modules
      if (currentModule?.module_type === 'medication') {
        return (
          <FormikContext.Consumer>
            {({ setFieldValue }) => (
              <div>
                <DeferredCatalogueStep
                  key="medication"
                  type="medication"
                  moduleData={currentFields}
                  {...sharedProps}
                  onPharmacyExtracted={(details) => handleMedicationPharmacyExtracted(details, setFieldValue)}
                />
              </div>
            )}
          </FormikContext.Consumer>
        );
      } else if (currentModule?.module_type === 'medication_allergies') {
        return (
          <FormikContext.Consumer>
            {({ setFieldValue }) => (
              <div>
                <DeferredCatalogueStep
                  key="medication_allergies"
                  type="medication_allergies"
                  moduleData={currentFields}
                  {...sharedProps}
                  onPharmacyExtracted={(details) => handleMedicationAllergiesPharmacyExtracted(details, setFieldValue)}
                />
              </div>
            )}
          </FormikContext.Consumer>
        );
      } else if (currentModule?.module_type === 'pharmacy') {
        return (
          <FormikContext.Consumer>
            {({ values }) => (
              <div>
                <DeferredCatalogueStep
                  key="pharmacy"
                  type="pharmacy"
                  moduleData={currentFields}
                  {...sharedProps}
                  extractedPharmacyDetails={[
                    ...(values.extractedPharmacyDetails || []),
                    ...(values.extractedPharmacyDetailsAllergies || [])
                  ]}
                />
              </div>
            )}
          </FormikContext.Consumer>
        );
      }

      return (
        <DynamicStepComponent
          {...sharedProps}
          step={step}
          moduleName={currentModule?.module_name || `Step ${step}`}
          moduleFields={currentFields}
          moduleData={currentModule}
          setCanvasGender={setCanvasGender}
        />
      );
    }

    // Only render pain diagram if feature is enabled and we're on that step
    if (isPainDiagramEnabled && step === sortedModules.length + 1) {
      return (
        <Step5Diagram
          ref={step5Ref}
          onSubmitWithPopup={handleSubmitWithPopup}
          canvasGender={canvasGender}
          setCanvasGender={setCanvasGender}
        />
      );
    }
    return null;
  };

  // Show initial loading
  // Show skeleton loading during initial load
  if (isInitialLoading) {
    return (
      <div style={{ minHeight: '100vh', paddingTop: '40px' }}>
        <SkeletonLoading />
      </div>
    );
  }

  // Never hide an initialization failure behind the loading skeleton.
  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', padding: '20px' }}>
        <ErrorDisplay error={error} />
        <button 
          type="button" onClick={() => setInitializationAttempt(attempt => attempt + 1)}
         className='retry-loading-btn'>
          Retry Loading
        </button>
      </div>
    );
  }

  // Show skeleton loading if fields are still being fetched
  if (sortedModules.length > 0 && Object.keys(fieldsMap).length === 0) {
    return (
      <div style={{ minHeight: '100vh', paddingTop: '40px' }}>
        <SkeletonLoading />
      </div>
    );
  }

  // Generate module names for progress indicator - show FULL names
  const moduleNames = sortedModules.map(mod => {
    const name = mod.module_name || '';
    // Clean up and format module names properly
    if (name.toLowerCase().includes('patient onboarding')) return 'Patient Information';
    if (name.toLowerCase().includes('medical history')) return 'Medical History';
    if (name.toLowerCase().includes('medication') && !name.toLowerCase().includes('allerg')) return 'Current Medications';
    if (name.toLowerCase().includes('family')) return 'Family History';
    if (name.toLowerCase().includes('pharmacy')) return 'Pharmacy';
    if (name.toLowerCase().includes('allerg')) return 'Allergies';
    if (name.toLowerCase().includes('review')) return 'Review of Systems';
    // Title case the name if no match
    return name.split(' ').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  });
  // Add Pain Diagram to progress indicator only if feature is enabled
  if (isPainDiagramEnabled) {
    moduleNames.push('Pain Diagram');
  }

  const getCurrentValidationSchema = () => {
    if (step >= 1 && step <= sortedModules.length) {
      const currentModule = sortedModules[step - 1];
      const currentFields = fieldsMap[currentModule?.module_name] || [];
      return generateYupSchema(currentFields);
    }
    return Yup.object({});
  };

  return (
    <div>
      <ToastNotification 
        message={toastMessage} 
        type={toastType} 
        onClose={() => setToastMessage(null)}/>
      
      <div className={`logo-img d-flex-center ${isPainDiagramEnabled && step === totalSteps ? 'hide' : ''}`}>
        <div style={{ textAlign: 'center', marginTop: '10px' }}>
          <BrandedLogo width="200px" height="auto" />
        </div>
      </div>

      {/* Visual progress indicator - only hide on Pain Diagram step (full-screen canvas) */}
      {!(isPainDiagramEnabled && step === totalSteps) && (
        <StepProgressIndicator
          currentStep={step}
          totalSteps={totalSteps}
          moduleNames={moduleNames}
        />
      )}

      {/* Apply dynamic container width class based on current module */}
      <div className={`form-container ${currentContainerWidth}`}>
        <ErrorDisplay error={error} onClose={() => setError(null)} />
        <CalendarPreload fields={currentCalendarFields} />

        {loading && (
          <div className='loading-container'>
            <div className='spinner-container'>
              <LoadingSpinner message="Submitting form..." />
            </div>
          </div>
        )}

        <Formik
          initialValues={dynamicInitialValues}
          validationSchema={getCurrentValidationSchema()}
          onSubmit={handleSubmit}
          enableReinitialize={true}  
          validateOnChange={true}
          validateOnBlur={true}
        >
          {({ values, errors, touched, validateForm, setFieldValue, setTouched, status }) => {
            // Compute active errors directly from Formik's live state
            const activeErrors = {};
            if (hasValidationError) {
              Object.keys(errors).forEach(key => {
                if (touched[key] && errors[key]) {
                  activeErrors[key] = errors[key];
                }
              });
              // If all errors are fixed, clear the flag
              if (Object.keys(activeErrors).length === 0 && Object.keys(errors).length === 0) {
                setTimeout(() => {
                  setHasValidationError(false);
                  setSubmitError(null);
                }, 0);
              }
            }

            const liveErrorDisplay = hasValidationError && Object.keys(activeErrors).length > 0 ? (
              <ErrorDisplay
                errorsObject={activeErrors}
                onClose={() => {
                  setSubmitError(null);
                  setHasValidationError(false);
                }}
              />
            ) : null;

            return (
            <Form onSubmit={(e) => e.preventDefault()} autoComplete="off" noValidate>
              {/* Auto-save form data as user fills the form */}
              <FormDataPersistence
                step={step}
                painLevels={painLevels}
                canvasGender={canvasGender}
                onFormModified={setHasFormBeenModified}
              />

              {status?.general && (
                <ErrorDisplay error={status.general} />
              )}

              {renderStep(step, { values, errors, touched, setFieldValue, errorDisplay: liveErrorDisplay })}

              <FormNavigation
                step={step}
                totalSteps={totalSteps}
                prevStep={prevStep}
                nextStep={
                  !catalogueRendererReady || !calendarReady ? () => {} : step === totalSteps
                    ? (isPainDiagramEnabled
                        ? () => handleStep5Submit(values, validateForm, setTouched)
                        : async () => {
                            // Pain diagram disabled - submit form directly from last module
                            setSubmitError(null);
                            setHasValidationError(false);
                            const formErrors = await validateForm();
                            if (Object.keys(formErrors).length > 0) {
                              setHasValidationError(true);
                              const touchedFields = Object.keys(formErrors).reduce((acc, key) => {
                                acc[key] = true;
                                return acc;
                              }, {});
                              setTouched(touchedFields);
                              setSubmitError(true);
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            } else {
                              activityLogger.stepCompleted(step, sortedModules[step - 1]?.module_name);
                              handleSubmit(values);
                            }
                          })
                    : () => handleNextStep(step, values, validateForm, setTouched)
                }
                loading={loading || isLoading}
                nextDisabled={!catalogueRendererReady || !calendarReady}
                isLoading={isLoading}
                formValid={Object.keys(errors).length === 0}
                hasError={hasValidationError}
              />
            </Form>
          );
          }}
        </Formik>
      </div>

      {/* Duplicate Patient Modal */}
      <DuplicatePatientModal
        isOpen={showDuplicateModal}
        patientName={duplicatePatientName}
        onFollowup={() => {
          setShowDuplicateModal(false);
          const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
          const slug = tenant?.slug || new URLSearchParams(window.location.search).get('tenant') || '';
          if (isLocal) {
            window.location.href = `http://localhost:3003/?tenant=${slug}`;
          } else if (duplicateFollowupDomain) {
            window.location.href = `https://${duplicateFollowupDomain}`;
          } else {
            window.location.href = `https://${window.location.hostname.replace('intake.', 'followup.').replace('newintake.', 'followup.')}`;
          }
        }}
        onClose={() => {
          setShowDuplicateModal(false);
          isSubmittingStep.current = false;
        }}
      />
    </div>
  );
};

export default FormPage;
